'use client';
/**
 * @file     ThemeToggle.tsx
 * @readme   ./README.md
 * @purpose  Header light/dark switch: one round icon button showing a sun in light mode and a moon in dark mode, morphing between them. Both glyphs render and the html data-theme attribute picks the visible one, so the icon is right before hydration.
 */

import { useTranslation } from 'react-i18next';
import { useAppTheme } from '@/core/theme/useAppTheme';
import styles from './ThemeToggle.module.css';

const SunGlyph = () => (
  <svg className={`${styles.glyph} ${styles.sun}`} width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true" focusable="false">
    <circle cx="10" cy="10" r="3.5" />
    <path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.34 4.34l1.42 1.42M14.24 14.24l1.42 1.42M4.34 15.66l1.42-1.42M14.24 5.76l1.42-1.42" />
  </svg>
);

const MoonGlyph = () => (
  <svg className={`${styles.glyph} ${styles.moon}`} width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d="M16.98 10.61A6.98 6.98 0 1 1 9.39 3.03a5.42 5.42 0 0 0 7.59 7.58Z" />
  </svg>
);

export const ThemeToggle = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const { mode, toggleMode } = useAppTheme();
  const label = mode === 'dark'
    ? t('nav.actions.switchToLight', { defaultValue: 'Switch to light mode' })
    : t('nav.actions.switchToDark', { defaultValue: 'Switch to dark mode' });

  return (
    <button
      type="button"
      className={className ? `${styles.toggle} ${className}` : styles.toggle}
      aria-label={label}
      data-tooltip={label}
      onClick={toggleMode}
    >
      <span className={styles.stage}>
        <SunGlyph />
        <MoonGlyph />
      </span>
    </button>
  );
};

export default ThemeToggle;
